import { useApiState } from 'hooks/useApiState';
import { useCallback, useEffect, useMemo, type JSX } from 'react';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { ChatState } from 'stores/atoms/chatState';
import type { VoiceChatEditFormStateType } from 'types/page/voiceChatEditFormStateType';
import type {
  MessageRole,
  WsChatSidebarConfig,
} from 'pages/ws/components/chatSidebar/WsChatSidebar';
import WsModalChatbotButton from 'pages/ws/components/modalChatbotButton/WsModalChatbotButton';
import { isEmpty } from 'utils/app';

interface WsVoiceChatEditChatbotProps {
  getValues: () => VoiceChatEditFormStateType;
  setValue: (
    name: keyof VoiceChatEditFormStateType,
    value: VoiceChatEditFormStateType[keyof VoiceChatEditFormStateType]
  ) => void;
  onCreateVoiceChatRef?: React.MutableRefObject<(() => void) | undefined>;
}

/**
 * ボイスチャット編集画面用のチャットボットコンポーネント（WsFormEditChatbot ミラー）。
 * テキスト生成のみ（TTS 音声生成はフェーズ2）。
 */
export default function WsVoiceChatEditChatbot({
  getValues,
  setValue,
  onCreateVoiceChatRef,
}: WsVoiceChatEditChatbotProps): JSX.Element {
  const [acctState, setAcctState] = useAtom(AcctState);
  const [chatState, setChatState] = useAtom(ChatState);
  const { postVoiceChatAiQuestions } = useApiState();

  /**
   * 設問設定エリアのフェードアウト効果を開始します。
   */
  const startContentFadeOut = useCallback(() => {
    const contentSections = document.querySelectorAll('[data-section="questions"]');
    contentSections.forEach(section => {
      section.classList.add('ai-fade-out');
    });
  }, []);

  /**
   * 設問設定エリアのフェードイン効果を開始します。
   */
  const startContentFadeIn = useCallback(() => {
    const contentSections = document.querySelectorAll('[data-section="questions"]');
    contentSections.forEach(section => {
      section.classList.remove('ai-fade-out');
      section.classList.add('ai-fade-in');
      setTimeout(() => {
        section.classList.remove('ai-fade-in');
      }, 600);
    });
  }, []);

  // チャット設定
  const chatConfig: WsChatSidebarConfig = useMemo(
    () => ({
      initialMessages: [
        '『誰向け』と『どんなため』かを一言で伝えていただければ、ピッタリのボイスチャットをご用意します。',
        `例えば…
・『来店者向けの満足度アンケートを音声で作成して』
・『イベント参加者向けの感想ヒアリングを作成して』
といった感じです。お気軽に書いてみてください！`,
      ],
      buttons: [],
      onMessage: async (message, setMessages, messages) => {
        const questions = getValues?.()?.questions ?? [];

        try {
          const response = await postVoiceChatAiQuestions({
            companyId: acctState.company.id,
            title: getValues?.()?.title ?? '',
            description: getValues?.()?.description ?? null,
            prompt: message,
            currentContent: {
              title: getValues?.()?.title ?? '',
              description: getValues?.()?.description ?? null,
              guidanceText: getValues?.()?.guidanceText ?? null,
              questions: questions.map(q => ({
                title: q.title ?? '',
                questionModeDiv: q.answerModeDiv,
                requiredFlag: q.requiredFlag ?? false,
                supplementFlag: q.supplementFlag ?? false,
                supplementPlaceholder: q.supplementPlaceholder ?? null,
              })),
            },
            history: messages,
          });

          if (isEmpty(response.answerContent)) {
            setMessages(prev => [
              ...prev,
              { role: 'ai' as MessageRole, text: response.answerText },
            ]);
            return;
          }

          startContentFadeOut();

          setTimeout(() => {
            setValue('title', response.answerContent.title ?? undefined);
            setValue('description', response.answerContent.description ?? undefined);
            setValue('guidanceText', response.answerContent.guidanceText ?? undefined);
            if (response.answerContent.guidanceText) {
              setValue('guidanceFlag', true);
            }

            const newQuestions = response.answerContent.questions.map(question => ({
              answerModeDiv: question.questionModeDiv,
              title: question.title ?? '',
              requiredFlag: question.requiredFlag ?? false,
              supplementFlag: question.supplementFlag ?? false,
              supplementPlaceholder: question.supplementPlaceholder ?? '',
            }));

            setValue(
              'questions',
              newQuestions as unknown as VoiceChatEditFormStateType['questions']
            );

            // 完了ページ（results[0] → completeXxx。converter と対称）
            const result = response.answerContent.results?.[0];
            if (result) {
              setValue('completeTitle', result.title);
              setValue('completeDescriptionHtml', result.descriptionHtml);
            }

            setTimeout(() => {
              startContentFadeIn();
            }, 100);
          }, 400);

          setMessages(prev => [...prev, { role: 'ai' as MessageRole, text: response.answerText }]);

          setAcctState(prev => ({
            ...prev,
            company: {
              ...prev.company,
              aiTextUsageCount: response?.aiTextUsageCount ?? acctState.company.aiTextUsageCount,
            },
          }));
        } catch (error) {
          console.error('AIボイスチャット生成エラー:', error);
          setMessages(prev => [
            ...prev,
            {
              role: 'ai' as MessageRole,
              text: '申し訳ございません。AIボイスチャットの生成中にエラーが発生しました。しばらく時間をおいてから再度お試しください。',
            },
          ]);
        }
      },
    }),
    [
      acctState.company,
      getValues,
      postVoiceChatAiQuestions,
      setAcctState,
      setValue,
      startContentFadeIn,
      startContentFadeOut,
    ]
  );

  // ページ離脱時にチャットの状態をリセット
  useEffect(() => {
    return () => {
      setChatState(prev => ({ ...prev, open: false, messages: [], config: null }));
    };
  }, [setChatState]);

  // チャットが開かれた時に chatConfig を設定
  useEffect(() => {
    if (chatState.open && !chatState.config) {
      setChatState(prev => ({ ...prev, config: chatConfig }));
    }
  }, [chatState.open, chatState.config, setChatState, chatConfig]);

  // AI によるボイスチャット作成を行う（新規作成時はメッセージをリセット）
  const onCreateVoiceChat = useCallback(() => {
    setChatState(prev => ({ ...prev, open: true, config: chatConfig, messages: [] }));
  }, [chatConfig, setChatState]);

  // 親コンポーネントから onCreateVoiceChat を呼び出せるようにする
  useEffect(() => {
    if (onCreateVoiceChatRef) {
      onCreateVoiceChatRef.current = onCreateVoiceChat;
    }
  }, [onCreateVoiceChat, onCreateVoiceChatRef]);

  // チャットを開く（既存のメッセージを保持）
  const openChat = useCallback(() => {
    setChatState(prev => ({ ...prev, open: true, config: chatConfig }));
  }, [chatConfig, setChatState]);

  return (
    <>
      {!chatState.open && <WsModalChatbotButton onOpen={openChat} title="AIでボイスチャット作成" />}
    </>
  );
}
